import { Input } from "@/src/components/ui/input";
import { Combobox } from "@/src/components/ui/combobox";
import type { UseFormReturn } from "react-hook-form";
import type { CheckoutFormData } from "@/src/lib/schemas/checkout-schemas";
import { cn } from "@/src/lib/utils";
import type { GooglePrediction } from "@tagadapay/plugin-sdk/react";

interface ShippingAddressFormProps {
  form: UseFormReturn<CheckoutFormData>;
  countries: Record<string, any>;
  availableStates: Array<{ code: string; name: string }>;
  isCountrySelected: boolean;
  addressInput: string;
  showPredictions: boolean;
  predictions: GooglePrediction[];
  onAddressInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPredictionSelect: (prediction: GooglePrediction) => void;
  onCountryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  sameBillingAddress: boolean;
  onSameBillingAddressChange: (checked: boolean) => void;
}

export function ShippingAddressForm({
  form,
  countries,
  availableStates,
  isCountrySelected,
  addressInput,
  showPredictions,
  predictions,
  onAddressInputChange,
  onPredictionSelect,
  onCountryChange,
  sameBillingAddress,
  onSameBillingAddressChange,
}: ShippingAddressFormProps) {
  const { watch, setValue, formState } = form;
  const errors = formState.errors;

  // Handler to sync billing from shipping when checkbox is checked
  const handleSameBillingChange = (checked: boolean) => {
    if (checked) {
      // Copy shipping fields to billing fields
      setValue("billingFirstName", watch("firstName"));
      setValue("billingLastName", watch("lastName"));
      setValue("billingAddress", watch("address1"));
      setValue("billingCountry", watch("country"));
      setValue("billingState", watch("state"));
      setValue("billingCity", watch("city"));
      setValue("billingZip", watch("postal"));
    }
    onSameBillingAddressChange(checked);
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* STEP 3 Header */}
      <div
        className="flex items-center"
        style={{
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "transparent",
          margin: "15px 0px 0px",
          padding: "0px",
          backgroundColor: "transparent",
        }}
      >
        <div>
          <div
            style={{
              color: "rgb(51, 51, 51)",
              fontFamily: "Montserrat",
              lineHeight: 1.2,
              margin: 0,
              padding: "8px 10px",
              textShadow: "transparent 0px 0px 0px",
              fontSize: "16px",
              textAlign: "left",
            }}
            className="text-sm sm:text-base md:text-[18px]"
          >
            <p>
              <strong>STEP 3: SHIPPING INFORMATION</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Country Select */}
      <div className="flex flex-col gap-2" style={{ margin: "8px 0px" }}>
        <Combobox
          options={Object.values(countries).map((country: any) => ({
            value: country.iso,
            label: country.name,
          }))}
          value={watch("country")}
          onValueChange={(countryCode: string) => {
            const syntheticEvent = {
              target: { value: countryCode },
            } as React.ChangeEvent<HTMLSelectElement>;
            onCountryChange(syntheticEvent);
          }}
          placeholder="-- Choose Country --"
          error={!!errors.country}
          data-address-field="country"
          data-type="shipping_country"
          className="h-12 sm:h-14 bg-white text-[rgb(37,42,50)] font-montserrat text-sm text-left font-normal not-italic border-gray-100 transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100"
        />
        {errors.country && (
          <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.country.message}</p>
        )}
      </div>

      {/* Full Address Input */}
      <div className="flex flex-col gap-2" style={{ margin: "8px 0px 0px" }}>
        <div className="relative">
          <Input
            id="address1"
            value={addressInput}
            onChange={onAddressInputChange}
            disabled={!isCountrySelected}
            placeholder="Full Address"
            data-address-field="address1"
            data-type="shipping_address"
            name="shipping_address"
            className={cn(
              "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100",
              errors.address1 &&
                "border-red-500 focus:border-red-500 focus:ring-red-100",
            )}
            style={{
              backgroundColor: "rgb(255, 255, 255)",
              color: "rgb(37, 42, 50)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              textAlign: "left",
              fontWeight: "normal",
              fontStyle: "normal",
            }}
          />

          {showPredictions && predictions.length > 0 && isCountrySelected && (
            <div className="absolute z-10 mt-1 max-h-48 sm:max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
              {predictions.map((prediction) => (
                <button
                  key={prediction.place_id}
                  type="button"
                  onClick={() => onPredictionSelect(prediction)}
                  className="w-full border-b border-gray-100 px-3 sm:px-4 py-2 sm:py-3 text-left last:border-b-0 hover:bg-gray-50"
                >
                  <div className="text-xs sm:text-sm font-medium">
                    {prediction.structured_formatting?.main_text}
                  </div>
                  <div className="text-[11px] sm:text-xs text-gray-600">
                    {prediction.structured_formatting?.secondary_text}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {errors.address1 && (
          <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.address1.message}</p>
        )}
      </div>

      {/* State, City, ZIP Row */}
      <div
        className="grid w-full grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-3"
        style={{ margin: "8px 0px 0px", padding: "0px" }}
      >
        <div className="flex w-full flex-col gap-2">
          {availableStates.length > 0 ? (
            <Combobox
              options={availableStates.map((state) => ({
                value: state.code,
                label: state.name,
              }))}
              value={watch("state")}
              onValueChange={(stateCode: string) =>
                setValue("state", stateCode)
              }
              placeholder="State/Province "
              error={!!errors.state}
              data-type="shipping_state"
              className="h-12 sm:h-14 bg-white text-[rgb(37,42,50)] font-montserrat text-sm text-left font-normal not-italic border-gray-100 transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
          ) : (
            <Input
              value={watch("state")}
              onChange={(e) => setValue("state", e.target.value)}
              disabled={!isCountrySelected}
              placeholder="State/Province "
              data-address-field="state"
              data-type="shipping_state"
              name="shipping_state"
              className={cn(
                "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100",
                errors.state &&
                  "border-red-500 focus:border-red-500 focus:ring-red-100",
              )}
              style={{
                backgroundColor: "rgb(255, 255, 255)",
                color: "rgb(37, 42, 50)",
                fontFamily: "Montserrat",
                fontSize: "14px",
                textAlign: "left",
                fontWeight: "normal",
                fontStyle: "normal",
              }}
            />
          )}
          {errors.state && (
            <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.state.message}</p>
          )}
        </div>

        <div className="flex w-full flex-col gap-2">
          <Input
            id="city"
            value={watch("city")}
            onChange={(e) => setValue("city", e.target.value)}
            disabled={!isCountrySelected}
            placeholder="City "
            data-address-field="city"
            data-type="shipping_city"
            name="shipping_city"
            className={cn(
              "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100",
              errors.city &&
                "border-red-500 focus:border-red-500 focus:ring-red-100",
            )}
            style={{
              backgroundColor: "rgb(255, 255, 255)",
              color: "rgb(37, 42, 50)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              textAlign: "left",
              fontWeight: "normal",
              fontStyle: "normal",
            }}
          />
          {errors.city && (
            <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.city.message}</p>
          )}
        </div>

        <div className="flex w-full flex-col gap-2">
          <Input
            value={watch("postal")}
            onChange={(e) => setValue("postal", e.target.value)}
            disabled={!isCountrySelected}
            placeholder="ZIP code "
            data-address-field="postal"
            data-type="shipping_zip"
            name="shipping_zip"
            className={cn(
              "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100",
              errors.postal &&
                "border-red-500 focus:border-red-500 focus:ring-red-100",
            )}
            style={{
              backgroundColor: "rgb(255, 255, 255)",
              color: "rgb(37, 42, 50)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              textAlign: "left",
              fontWeight: "normal",
              fontStyle: "normal",
            }}
          />
          {errors.postal && (
            <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.postal.message}</p>
          )}
        </div>
      </div>

      {/* Billing Address Section */}
      <div
        style={{
          fontFamily: "Montserrat",
          lineHeight: 1,
          margin: "12px 0px 0px",
          padding: "8px 10px",
          textShadow: "transparent 0px 0px 0px",
          fontSize: "16px",
          textAlign: "left",
        }}
        className="text-sm sm:text-base md:text-[18px]"
      >
        <p>
          <strong>Billing Address</strong>
        </p>
      </div>

      <div
        style={{
          fontFamily: "Montserrat",
          lineHeight: 1,
          margin: "0px",
          padding: "6px 10px",
          textShadow: "transparent 0px 0px 0px",
          fontSize: "14px",
          textAlign: "left",
        }}
        className="text-xs sm:text-sm md:text-[15px]"
      >
        <p>Select the address that matches your card or payment method.</p>
      </div>

      {/* Same as Shipping Address Checkbox */}
      <div style={{ margin: "10px 0px 0px" }}>
        <label
          className="flex cursor-pointer items-center"
          style={{
            backgroundColor: "transparent",
            color: "rgb(37, 42, 50)",
            fontFamily: "Montserrat",
            fontSize: "14px",
            textAlign: "left",
            fontWeight: "normal",
            fontStyle: "normal",
          }}
        >
          <input
            type="checkbox"
            name="same_billing"
            data-type="custom"
            data-name="same_billing"
            checked={sameBillingAddress}
            onChange={(e) => handleSameBillingChange(e.target.checked)}
            className="mr-2 h-4 w-4 cursor-pointer"
          />
          <span
            style={{
              color: "rgb(37, 42, 50)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              textAlign: "left",
              fontWeight: "normal",
              fontStyle: "normal",
            }}
          >
            Same as shipping address
          </span>
        </label>
      </div>

      {/* Billing Address Fields Container */}
      <div
        id="billing_container"
        className={cn("space-y-3 sm:space-y-4", sameBillingAddress && "hidden")}
        style={{
          borderWidth: "0px",
          borderStyle: "solid",
          borderRadius: "6px",
          margin: "8px 0px 0px",
          padding: "5px",
          backgroundColor: "transparent",
        }}
      >
        <div className="flex flex-col gap-2" style={{ margin: "0px" }}>
          <Input
            value={watch("billingFirstName") || ""}
            onChange={(e) => setValue("billingFirstName", e.target.value)}
            placeholder="First name "
            data-type="first_name"
            name="first_name"
            className={cn(
              "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
              errors.billingFirstName &&
                "border-red-500 focus:border-red-500 focus:ring-red-100",
            )}
            style={{
              backgroundColor: "rgb(255, 255, 255)",
              color: "rgb(37, 42, 50)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              textAlign: "left",
              fontWeight: "normal",
              fontStyle: "normal",
            }}
          />
          {errors.billingFirstName && (
            <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">
              {errors.billingFirstName.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2" style={{ margin: "8px 0px 0px" }}>
          <Input
            value={watch("billingLastName") || ""}
            onChange={(e) => setValue("billingLastName", e.target.value)}
            placeholder="Last name "
            data-type="last_name"
            name="last_name"
            className={cn(
              "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
              errors.billingLastName &&
                "border-red-500 focus:border-red-500 focus:ring-red-100",
            )}
            style={{
              backgroundColor: "rgb(255, 255, 255)",
              color: "rgb(37, 42, 50)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              textAlign: "left",
              fontWeight: "normal",
              fontStyle: "normal",
            }}
          />
          {errors.billingLastName && (
            <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">
              {errors.billingLastName.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2" style={{ margin: "8px 0px 0px" }}>
          <Input
            value={watch("billingAddress") || ""}
            onChange={(e) => setValue("billingAddress", e.target.value)}
            placeholder="Billing Address"
            data-type="address"
            name="address"
            data-auto_optin="true"
            className={cn(
              "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
              errors.billingAddress &&
                "border-red-500 focus:border-red-500 focus:ring-red-100",
            )}
            style={{
              backgroundColor: "rgb(255, 255, 255)",
              color: "rgb(37, 42, 50)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              textAlign: "left",
              fontWeight: "normal",
              fontStyle: "normal",
            }}
          />
          {errors.billingAddress && (
            <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">
              {errors.billingAddress.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2" style={{ margin: "8px 0px 0px" }}>
          <Combobox
            options={Object.values(countries).map((country: any) => ({
              value: country.iso,
              label: country.name,
            }))}
            value={watch("billingCountry") || ""}
            onValueChange={(countryCode: string) =>
              setValue("billingCountry", countryCode)
            }
            placeholder="Country "
            error={!!errors.billingCountry}
            data-type="country"
            className="h-12 sm:h-14 bg-white text-[rgb(37,42,50)] font-montserrat text-sm text-left font-normal not-italic border-gray-100 transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100"
          />
          {errors.billingCountry && (
            <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">
              {errors.billingCountry.message}
            </p>
          )}
        </div>

        <div
          className="grid w-full grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-3"
          style={{ margin: "8px 0px 0px", padding: "0px" }}
        >
          <div className="flex w-full flex-col gap-2">
            <Input
              value={watch("billingState") || ""}
              onChange={(e) => setValue("billingState", e.target.value)}
              placeholder="State"
              data-type="state"
              name="state"
              className={cn(
                "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
                errors.billingState &&
                  "border-red-500 focus:border-red-500 focus:ring-red-100",
              )}
              style={{
                backgroundColor: "rgb(255, 255, 255)",
                color: "rgb(37, 42, 50)",
                fontFamily: "Montserrat",
                fontSize: "14px",
                textAlign: "left",
                fontWeight: "normal",
                fontStyle: "normal",
              }}
            />
            {errors.billingState && (
              <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">
                {errors.billingState.message}
              </p>
            )}
          </div>

          <div className="flex w-full flex-col gap-2">
            <Input
              value={watch("billingCity") || ""}
              onChange={(e) => setValue("billingCity", e.target.value)}
              placeholder="City"
              data-type="city"
              name="city"
              data-auto_optin="true"
              className={cn(
                "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
                errors.billingCity &&
                  "border-red-500 focus:border-red-500 focus:ring-red-100",
              )}
              style={{
                backgroundColor: "rgb(255, 255, 255)",
                color: "rgb(37, 42, 50)",
                fontFamily: "Montserrat",
                fontSize: "14px",
                textAlign: "left",
                fontWeight: "normal",
                fontStyle: "normal",
              }}
            />
            {errors.billingCity && (
              <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">
                {errors.billingCity.message}
              </p>
            )}
          </div>

          <div className="flex w-full flex-col gap-2">
            <Input
              value={watch("billingZip") || ""}
              onChange={(e) => setValue("billingZip", e.target.value)}
              placeholder="Zip"
              data-type="zip"
              name="zip"
              className={cn(
                "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
                errors.billingZip &&
                  "border-red-500 focus:border-red-500 focus:ring-red-100",
              )}
              style={{
                backgroundColor: "rgb(255, 255, 255)",
                color: "rgb(37, 42, 50)",
                fontFamily: "Montserrat",
                fontSize: "14px",
                textAlign: "left",
                fontWeight: "normal",
                fontStyle: "normal",
              }}
            />
            {errors.billingZip && (
              <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">
                {errors.billingZip.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
